import { useMediaQuery } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { authSelectors } from 'ducks/auth'
import { chatDrawerActions, chatDrawerSelectors } from 'ducks/chatDrawer'
import { useAdaPositioning } from 'hooks/useAdaPositioning'
import { useAdaActive } from 'layout/widgets/adaChat/useAdaActive'
import { CloseIcon } from 'opensolar-ui'
import { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import { setIgnoreClickOutside } from 'reducer/globalCart'
import { Z_INDEX_CONFIGS } from 'Themes'
import type { Theme } from 'types/themes'
import { useVoice } from 'voice/OpenAIRealtimeVoiceAPI/VoiceContext'
import AdaIcon from '../../layout/widgets/adaChat/AdaIcon'

const useStyles = makeStyles(() => ({
  floatingContainer: {
    position: 'fixed',
    bottom: 60,
    right: 20,
    zIndex: Z_INDEX_CONFIGS.adaChatDrawer,
    cursor: 'pointer',
    '@media (hover: hover)': {
      '&:hover $closeButton': {
        opacity: 1,
      },
    },
  },
  highPosition: {
    bottom: 70,
  },
  standardPosition: {
    bottom: 20,
  },
  closeButton: {
    position: 'absolute',
    top: -15,
    right: -8,
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transition: 'all 0.2s ease',
    '@media (hover: none)': {
      opacity: 1,
    },
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'scale(1.1)',
      },
    },
    '& svg': {
      fontSize: 16,
      color: '#666666',
    },
  },
  unreadBadge: {
    position: 'absolute',
    bottom: -2,
    left: -2,
    minWidth: 16,
    height: 16,
    borderRadius: '50%',
    background: '#4272DD',
    color: 'white',
    fontSize: 10,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid white',
  },
}))

const useVisibilityState = () => {
  const chatDrawerOpen = useSelector(chatDrawerSelectors.getChatDrawerOpen)
  const {
    isOnDesignPage,
    isOnExplorePage,
    isOnHomePage,
    isInControlZone,
    isOnProposalPage,
    isOnPaymentRequestsPage,
  } = useAdaPositioning()

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true })

  const isPro = useSelector(authSelectors.getIsAuthenticatedPro)

  const isInLeadCaptureWidget = !!(
    document.getElementById('opensolar_lead_capture') ||
    document.getElementById('widget-form-placeholder-os') ||
    document.getElementById('widget-form')
  )

  const isInRegistrationForm =
    window.location.pathname.includes('/register') || window.location.pathname.includes('register-multistep')

  return {
    shouldHide:
      isOnDesignPage ||
      chatDrawerOpen ||
      isMobile ||
      isInLeadCaptureWidget ||
      isInRegistrationForm ||
      isOnPaymentRequestsPage ||
      !isPro,
    isOnDesignPage,
    isOnHomePage,
    isOnExplorePage,
    isInControlZone,
    chatDrawerOpen,
    isMobile,
    isInLeadCaptureWidget,
    isOnProposalPage,
    isOnPaymentRequestsPage,
  }
}

const useAdaStates = () => {
  const messages = useSelector(chatDrawerSelectors.getChatDrawerMessages)
  const chatDrawerOpen = useSelector(chatDrawerSelectors.getChatDrawerOpen)
  const unreadCount = useSelector(chatDrawerSelectors.getUnreadCount)
  const hasUnreadMessages = useSelector(chatDrawerSelectors.getHasUnreadMessages)

  const { isResponding: isVoiceResponding, isListening: isVoiceListening } = useVoice()

  const getIsAwaitingTextResponse = () => {
    const lastMessage = messages[messages.length - 1]
    return lastMessage && lastMessage.direction === 'send' && !chatDrawerOpen
  }

  return {
    isThinking: getIsAwaitingTextResponse(),
    isResponding: isVoiceResponding,
    isListening: isVoiceListening,
    unreadCount: unreadCount,
    hasUnreadMessages: hasUnreadMessages,
  }
}

const useChatDrawerToggle = () => {
  const dispatch = useDispatch()

  return () => {
    dispatch(chatDrawerActions.setChatDrawerOpen(true))
  }
}

const FloatingAdaIcon = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const dispatch = useDispatch()
  const {
    shouldHide,
    isOnDesignPage,
    isOnHomePage,
    isOnExplorePage,
    isInControlZone,
    isOnProposalPage,
  } = useVisibilityState()
  const { shouldElevate } = useAdaPositioning()
  const { isThinking, isResponding, isListening, unreadCount, hasUnreadMessages } = useAdaStates()
  const toggleChatDrawer = useChatDrawerToggle()
  const { active: enableAdaButton } = useAdaActive()

  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    if (!shouldHide) {
      setIsDismissed(false)
    }
  }, [shouldHide])

  if (shouldHide || !enableAdaButton || isDismissed) {
    return null
  }

  const containerClasses = `${classes.floatingContainer} ${
    shouldElevate ? classes.highPosition : classes.standardPosition
  }`

  const handleCloseClick = () => {
    // This is here to prevent GlobalCart from closing when FloatingAdaIcon is dismissed and removed from DOM
    dispatch(setIgnoreClickOutside(true))
    setIsDismissed(true)
  }

  const handleIconClick = () => {
    toggleChatDrawer()
  }

  return (
    <div className={containerClasses}>
      <div onClick={handleIconClick} title={translate('Chat with Ada')}>
        <AdaIcon
          size={36}
          showHoverEffects={true}
          withShadow={true}
          isThinking={isListening || isThinking}
          isResponding={isResponding}
        />
        {hasUnreadMessages && <div className={classes.unreadBadge}>{unreadCount > 9 ? '9+' : unreadCount}</div>}
      </div>
      <div className={classes.closeButton} onClick={handleCloseClick} title={translate('Dismiss')}>
        <CloseIcon />
      </div>
    </div>
  )
}

export default FloatingAdaIcon
